import { nanoid } from 'nanoid';
import {
  initModal,
  destroyModal,
  $modals,
  closeModal,
  openModal,
  toggleModal,
  changeModal,
  $modalZIndex,
  addZIndex,
  removeZIndex
} from './model';
import { useStoreMap, useStore } from 'effector-react';
import { useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';

type ModalCore = {
  id?: string;
};
export const useModalCore = ({ id: _id }: ModalCore = {}) => {
  const [id] = useState<string>(_id || nanoid());

  useEffect(() => {
    initModal(id);
    return () => {
      destroyModal(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useStoreMap({
    store: $modals,
    keys: [id],
    fn: (modals, [id]) => modals[id] ?? false
  });
  return { id, active };
};

export interface UseModalParamsType {
  id?: string;
  value?: boolean;
  close?: () => void;
  open?: () => void;
}

export const useModal = (params?: UseModalParamsType) => {
  const { id: _id, value } = params || {};
  const { id, active } = useModalCore({ id: _id });
  useEffect(() => {
    if (value !== undefined) {
      if (value) open();
      else close();
    }
  }, [value]);
  const close = () => {
    closeModal(id);
    if (params?.close) params.close();
  };
  const open = () => {
    openModal(id);
    if (params?.open) params.open();
  };
  const toggle = () => {
    toggleModal(id);
  };
  const change = (value: boolean) => {
    changeModal({ id, value });
  };
  return {
    id,
    active,
    close,
    open,
    toggle,
    change
  };
};

export type ReturnModalType = ReturnType<typeof useModal>;

export const useMaxHeightModal = (className: string, active: boolean) => {
  const [height, setHeight] = useState<number | string>('100%');
  useEffect(() => {
    if (active) {
      setTimeout(() => {
        const div = document.querySelector(`.${className}`);
        const top = div?.getBoundingClientRect()?.top || 0;
        setHeight(window.innerHeight - top);
      }, 300);
    }
    return () => {
      if (active) {
        setHeight('100%');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  return height;
};

export const useZIndexModal = (update?: any, show?: boolean) => {
  const [zIndex, setZIndex] = useState(0);
  const zI = useStore($modalZIndex);

  useEffect(() => {
    if (show) {
      setZIndex(zI[zI.length - 1]);
      addZIndex();

      return () => {
        setZIndex(0);
        removeZIndex();
      };
    }
  }, [show]);

  useEffect(() => {
    setZIndex(zI[zI.length - 1]);
    addZIndex();
    return () => {
      setZIndex(0);
      removeZIndex();
    };
  }, [update]);

  return zIndex;
};

export const useModalScrollLock = (value: boolean) => {
  useEffect(() => {
    const html = document.querySelector('html').style;
    const scroll = window.scrollY;
    if (value && isMobileOnly) {
      html.marginTop = `${scroll}`;
      html.overflow = 'hidden';
      html.left = '0';
      html.right = '0';
      html.top = '0';
      html.bottom = '0';
      html.position = 'fixed';
    }
    return () => {
      if (value && isMobileOnly) {
        html.marginTop = `0`;
        html.overflow = 'visible';
        html.left = 'auto';
        html.right = 'auto';
        html.top = 'auto';
        html.bottom = 'auto';
        html.position = 'static';
        window.scrollTo(0, scroll);
      }
    };
  }, [value]);
};
